import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit, PipeTransform, Type, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { filterPredicateFor, sortingDataAccessorFor } from '@shared/components/generictable/generictable-sorting';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { aciesDateFormat } from '@shared/helpers';
import { UserToNamePipe } from '@shared/pipes/user-to-name.pipe';
import { RacksetTransferService } from '@shared/services/export-import-services/rackset';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { IRackDetails, IRackReference, IRacks, IRackset, IResponseRack, IResponseRackset, RackingService } from '@shared/services/racking.service';
import { catchError, map, Subject, switchMap, takeUntil, tap, throwError } from 'rxjs';
import { RackingCreateUpdateRacksetDialogComponent } from './racking-create-update/racking-create-update-rackset';

@Component({
	selector: 'app-racking-racksets',
	templateUrl: './racking.racksets.component.html',
	styleUrls: ['./racking.racksets.component.scss'],
	animations: [trigger('detailExpand', [state('collapsed, void', style({ height: '0px' })), state('expanded', style({ height: '*' })), transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')), transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))])],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class RackingRacksetsComponent implements OnInit {
	private unsubscribe: Subject<void> = new Subject<void>();
	private racksetPriorities: Map<string, Map<number, string>> = new Map<string, Map<number, string>>();

	private expandedElementIds: Set<string> = new Set<string>();
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	public isAllSelected: boolean = false;
	public showSearch: boolean = false;
	public dataSource = new MatTableDataSource<IRackset>();
	public isLoading: boolean = false;
	public rackDetails: Map<string, IRackDetails> = new Map<string, IRackDetails>();

	public racks: Map<string, IResponseRack> = new Map<string, IResponseRack>();
	public expandedElements: IRackset[] = [];

	@ViewChild(MatPaginator) paginator: MatPaginator;

	/**
	 * Couples the sort to the data source the moment the table is there.
	 *
	 * The table stands inside an `@if` on the data, so it does not exist when the data lands, and a coupling made in the load callback found no sort on first open: the header arrows turned and no row moved until the data was loaded again. A setter is told whenever the query result changes, which is when the table
	 * appears.
	 */
	@ViewChild(MatSort) set sort(sort: MatSort | null) {
		this.dataSource.sort = sort ?? null;
	}

	get sort(): MatSort | null {
		return this.dataSource.sort;
	}

	columnsToDisplay = [
		{ name: 'name', translateKey: 'masterdata.racking.name' },
		{ name: 'description', translateKey: 'masterdata.racking.description' },
		{ name: 'updatedDate', translateKey: 'masterdata.racking.updatedDate' },
		{
			name: 'updatedBy',
			translateKey: 'masterdata.racking.updatedBy',
			pipe: UserToNamePipe,
		},
	];

	displayedColumns = this.columnsToDisplay.map((col) => col.name);

	innerColumnsToDisplay = [
		{ name: 'name', translateKey: 'masterdata.racking.name' },
		{ name: 'description', translateKey: 'masterdata.racking.description' },
		{ name: 'priority', translateKey: 'masterdata.racking.priority' },
		{ name: 'cells', translateKey: 'masterdata.racking.cells' },
		{ name: 'rows', translateKey: 'masterdata.racking.rows' },
		{ name: 'columns', translateKey: 'masterdata.racking.columns' },
		{
			name: 'components',
			translateKey: 'masterdata.racking.components.label',
		},
	];

	innerDisplayedColumns = this.innerColumnsToDisplay.map((col) => col.name);

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_expand_all',
			text: () => 'masterdata.racking.toggle-expand-btn',
			click: () => this.toggleAll(),
			disabled: () => this.dataSource?.data.length === 0,
			hideOnDisable: () => true,
		},
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.racking.newbtn.rackset',
			click: () => this.openCreateEditUpdateRackset(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_export',
			text: () => 'masterdata.racking.racksetImportExport.export.button',
			click: () => this.exportRacksets(),
			permissions: () => ['racking:RacksetList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.racking.racksetImportExport.import.button',
			click: () => this.onImport(),
			permissions: () => ['racking:RacksetPost', 'racking:RacksetPut', 'racking:RacksetList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.racking.racksetImportExport.import.template.button',
			click: () => this.racksetTransferService.downloadTemplate(),
			permissions: () => ['racking:RacksetList'],
		},
	];

	public constructor(
		public dialog: MatDialog,
		public cd: ChangeDetectorRef,
		private translator: TranslateService,
		private rackingService: RackingService,
		private notificationService: NotificationService,
		private racksetTransferService: RacksetTransferService,
		private importValidatorService: ImportValidatorService,
		private injector: Injector,
	) {
		// This list has a table of its own rather than app-generictable, so it has to sort the way that
		// table sorts: the updated column by the epoch kept beside the formatted date, and the editor
		// column by the name the pipe shows rather than the id the row holds. And filter the way it
		// filters, with the epoch kept out of the free text
		const columns = () => [
			{ name: 'updatedDate', dataKey: 'updatedDate', sortKey: 'updatedAtEpoch' },
			{ name: 'updatedBy', dataKey: 'updatedBy', pipe: UserToNamePipe, sortAsShown: true },
		];
		this.dataSource.sortingDataAccessor = sortingDataAccessorFor({
			columns,
			inherited: this.dataSource.sortingDataAccessor,
			pipeFor: (pipe: Type<PipeTransform>) => this.injector.get(pipe, null) ?? new pipe(),
			translate: (key: string) => this.translator.instant(key),
		});
		this.dataSource.filterPredicate = filterPredicateFor(columns, this.dataSource.filterPredicate);
	}

	public exportRacksets(): void {
		this.rackingService
			.getRacksets()
			.pipe(
				switchMap((racksets) => this.racksetTransferService.exportData(racksets.items)),
				takeUntil(this.unsubscribe),
			)
			.subscribe({
				next: () => {
					this.notificationService.notify(['masterdata.racking.racksetImportExport.export.notification.success.header'], ['masterdata.racking.racksetImportExport.export.notification.success.content'], 'success');
				},
				error: (error: Error) => {
					console.error('Failed to export racksets', error);
					this.notificationService.notify(['masterdata.racking.racksetImportExport.export.notification.error.header'], ['masterdata.racking.racksetImportExport.export.notification.error.content'], 'error');
					this.dialog.open(TransferErrorPopupComponent, {
						width: '450px',
						data: { message: error.message },
					});
				},
			});
	}

	public onImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.racking.racksetImportExport.import.title',
				subHeadingKey: 'masterdata.racking.racksetImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.racking.racksetImportExport.import.button',
				notificationHeader: 'masterdata.racking.racksetImportExport.import.notification.success.header',
				notificationContent: 'masterdata.racking.racksetImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.racking.racksetImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.racking.racksetImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.racking.racksetImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.racking.racksetImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.importValidatorService,
				data: this.racksetTransferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	ngOnInit(): void {
		this.isLoading = true;
		this.fetchDetails();
		this.loadData();

		this.displayedColumns.push('actions');

		this.innerDisplayedColumns.push('extendedActions');
	}

	ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	dropInnerColumn(event: CdkDragDrop<IRackReference[]>, rackset: IRackset) {
		const previousIndex = event.previousIndex;
		const currentIndex = event.currentIndex;

		rackset.racks.data = this.reorderRackPriorities(rackset, previousIndex, currentIndex);

		this.rackingService
			.updateRackset(rackset)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((responseRackset) => {
				this.expandedElementIds = new Set(this.expandedElements.map((e) => e.self));
				this.updateRacksetMetaData(responseRackset);
				this.updateRacksetInDataSource(rackset);
			});

		this.cd.detectChanges();
	}

	private updateRacksetMetaData(responseRackset: IResponseRackset) {
		// Exit early if responseRackset is null
		if (!responseRackset) {
			return;
		}

		// Find the index of the rackset in the dataSource
		const index = this.dataSource.data.findIndex((rs) => rs.self === responseRackset.self);

		// Check if the index is valid
		if (index === -1) {
			console.error('Rackset not found in dataSource.');
			return;
		}

		// Safely update the metadata for the found rackset
		this.dataSource.data[index].updatedBy = this.rackingService.getLastSegment(responseRackset.meta.updatedBy.self);

		this.dataSource.data[index].updatedDate = aciesDateFormat(new Date(responseRackset.meta.updatedAt), true, false);
		this.dataSource.data[index].updatedAtEpoch = responseRackset.meta.updatedAt;
	}

	private updateRacksetInDataSource(updatedRackset: IRackset) {
		// Find the index of the rackset in the data source
		const index = this.dataSource.data.findIndex((rs) => rs.self === updatedRackset.self);

		if (index > -1) {
			// Replace the old rackset with the updated one
			this.expandedElementIds = new Set(this.expandedElements.map((e) => e.self));
			this.dataSource.data[index] = updatedRackset;
			this.dataSource._updateChangeSubscription();
			this.expandedElements = this.dataSource.data.filter((ds) => this.expandedElementIds.has(ds.self));
		}
	}

	private reorderRackPriorities(rackset: IRackset, previousIndex: number, currentIndex: number): IRackReference[] {
		const rackPriorities = this.racksetPriorities.get(rackset.self);

		if (!rackPriorities) return [];

		const rackArray = Array.from(rackPriorities.entries());
		const [removedRack] = rackArray.splice(previousIndex, 1);

		rackArray.splice(currentIndex, 0, removedRack);

		const reorderedRackPriorities = new Map<number, string>();
		rackArray.forEach(([_, rackId], index) => {
			reorderedRackPriorities.set(index + 1, rackId);
		});

		this.racksetPriorities.set(rackset.self, reorderedRackPriorities);

		return this.convertRackPrioritiesToRackReferences(reorderedRackPriorities);
	}

	private convertRackPrioritiesToRackReferences(rackPriorities: Map<number, string>): IRackReference[] {
		const updatedRacks: IRackReference[] = [];
		rackPriorities.forEach((rackSelf, priority) => {
			updatedRacks.push({
				rack: { self: rackSelf },
				priority,
			});
		});
		return updatedRacks;
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong.
			console.error(`Backend returned code ${error.status}, body was: `, error.error);
		}
		// Return an observable with a user-facing error message.
		return throwError(() => new Error('Something bad happened; please try again later.'));
	}

	onRemoveRackClick(rackset: IRackset, rackRef: IRackReference) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.racking.remove.racks.heading'),
				content: this.translator.instant('masterdata.racking.remove.racks.content', {
					name: this.getDetails(rackRef.rack.self, 'name'),
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					const remove = rackRef.rack.self;

					// Update racks and priorities in a single loop
					const updatedRacks = rackset.racks.data
						.filter((ref) => ref.rack.self !== remove)
						.map((ref, index) => ({
							rack: { self: ref.rack.self },
							priority: index + 1,
						}));

					// Update rackset's data and selected racks
					rackset.racks.data = updatedRacks;
					rackset.selectedRacks = updatedRacks.map((ref) => ref.rack.self);

					// Update rack priorities in racksetPriorities using reduce
					const rackPriorityMap = updatedRacks.reduce((map, ref, idx) => {
						map.set(idx + 1, ref.rack.self);
						return map;
					}, new Map<number, string>());
					this.racksetPriorities.set(rackset.self, rackPriorityMap);

					// Update the dataSource
					const index = this.dataSource.data.findIndex((rs) => rs.self === rackset.self);
					if (index !== -1) {
						this.dataSource.data[index] = rackset;
						this.dataSource.data = [...this.dataSource.data];
					}

					this.rackingService
						.removeRackFromRackset(rackset)
						.pipe(catchError(this.handleError), takeUntil(this.unsubscribe))
						.subscribe(() => {
							this.notificationService.notify(['masterdata.racking.remove.racks.notification.header'], ['masterdata.racking.remove.racks.notification.content', { name: this.getDetails(remove, 'name') }], 'success');
						});
				}
			});
	}

	onDeleteRacksetClick(rackset: IRackset) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.racking.delete.racksets.heading'),
				content: this.translator.instant('masterdata.racking.delete.racksets.content', {
					name: rackset.name,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.rackingService
						.delete(rackset.self)
						.pipe(catchError(this.handleError))
						.subscribe(() => {
							this.notificationService.notify(['masterdata.racking.delete.racksets.notification.header'], ['masterdata.racking.delete.racksets.notification.content', { name: rackset.name }], 'success');
							this.loadData();
						});
				}
			});
	}

	openCreateEditUpdateRackset(rackset: object = null) {
		const dialogRef = this.dialog.open(RackingCreateUpdateRacksetDialogComponent, {
			disableClose: true,
			width: '650px',
			data: rackset,
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.loadData();
		});
	}

	toRackset(rackset: IResponseRackset) {
		const body: IRackset = {
			self: rackset.self,
			name: rackset.name,
			description: rackset.description,
			racks: new MatTableDataSource<IRackReference>(rackset.racks),
			selectedRacks: rackset.racks.map((obj) => obj.rack.self),
			updatedDate: aciesDateFormat(new Date(rackset.meta.updatedAt), true, false),
			updatedAtEpoch: rackset.meta.updatedAt,
			updatedBy: this.rackingService.getLastSegment(rackset.meta.updatedBy.self),
		};
		return body;
	}

	applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();
	}

	fetchDetails() {
		this.rackingService
			.getRacks()
			.pipe(
				takeUntil(this.unsubscribe),
				map((response: IRacks) => response.items),
				tap((racks) => {
					racks.forEach((rack) => {
						this.rackDetails.set(rack.self, {
							name: rack.name,
							description: rack.description,
							cells: rack.configuration.cells.toString(),
							rows: rack.configuration.rows.toString(),
							columns: rack.configuration.columns.toString(),
							components: rack.components
								.map((component) => {
									const parts: string[] = component.type.self.split('/');
									const lastPart: string = parts[parts.length - 1];
									const translation: string = `masterdata.racking.components.${lastPart}`;
									return this.translator.instant(translation);
								})
								.join(', '),
							updatedDate: aciesDateFormat(new Date(rack.meta.updatedAt), true, false),
						});
					});
				}),
			)
			.subscribe();
	}

	getPriority(racksetStr: string, rackStr: string): number {
		for (const rackset of this.dataSource.data) {
			if (rackset.self === racksetStr) {
				for (const rack of rackset.racks.data) {
					if (rack.rack.self === rackStr) {
						if ('priority' in rack) {
							return rack.priority;
						}
					}
				}
			}
		}
		return null; // or another default value if no match is found
	}

	getDetails(self: string, property: string): string {
		const rack = this.rackDetails.get(self);
		if (rack) {
			let current: string | any = rack;
			if (property in current) {
				current = current[property];
			} else {
				return undefined;
			}
			return current;
		}
		return undefined;
	}

	loadData() {
		this.isLoading = true;

		this.expandedElementIds = new Set(this.expandedElements.map((e) => e.self));

		this.rackingService
			.getRacksets()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((response) => {
				response.items.forEach((rackset) => {
					this.racksetPriorities.set(rackset.self, new Map<number, string>());

					// Iterate over each rack and set priority within the map
					rackset.racks.forEach((rack) => {
						// Get the map of priorities for this rackset
						const rackPrioritiesMap = this.racksetPriorities.get(rackset.self);

						// If the map exists, set the priority for the current rack
						if (rackPrioritiesMap) {
							rackPrioritiesMap.set(rack.priority, rack.rack.self);
						}
					});
				});
				this.dataSource.data = response.items.map((rackset) => this.toRackset(rackset));
				this.dataSource.paginator = this.paginator;
				this.expandedElements = this.dataSource.data.filter((ds) => this.expandedElementIds.has(ds.self));
				this.isLoading = false;
			});
	}

	toggleAll() {
		if (this.expandedElements.length < this.dataSource.data.length) {
			this.expandedElements = this.dataSource.data.slice();
		} else {
			this.expandedElements = [];
		}
	}

	toggleRow(element: IRackset) {
		if (element.racks && element.racks.data.length) {
			const idx = this.expandedElements.indexOf(element);
			if (idx > -1) {
				this.expandedElements.splice(idx, 1);
			} else {
				this.expandedElements.push(element);
			}
			this.cd.detectChanges();
		}
	}
}
